import CircleIcon from "@mui/icons-material/Circle";
import { styled, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import useFormatters from "./useFormaters";
import { forwardRef, Fragment } from "react";

export const StyledTable = styled("table")({
  width: "100%",
  borderSpacing: "0",
  fontSize: "8.96px",
  fontFamily:
    '"Roboto", "HelveticaNeue-Light", "Helvetica Neue Light", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
  fontWeight: 400,
  "& tr, & td, & th": {
    borderSpacing: 0,
  },
  "& col": {
    "&.col-first": {
      width: "10em",
    },
    "&.col-date": {
      width: "7em",
    },
    "&.col-large": {
      width: "10em",
    },
    "&.col-thin": {
      width: "4em",
    },
  },
  "& th": {
    fontWeight: 400,
    textAlign: "center",
  },
  "& td": {
    textAlign: "right",
  },
  "& th, & td": {
    padding: "0 8px",
    padding: "0 2px",
    "&.text-start": {
      textAlign: "left",
    },
    "&.text-center": {
      textAlign: "center",
    },
    "&.hide-span": {
      opacity: 0,
    },
    "&.visible-span": {
      borderWidth: "0 1px 1px 0",
      borderStyle: "solid",
      borderColor: "lightgrey",
    },
    "&.title": {
      backgroundColor: "#148668",
      color: "#fff",
    },
  },
  "& tr": {
    "&.fund-row": {
      backgroundColor: "#148668",
      color: "#fff",
    },
    "&.deal-status-row": {
      backgroundColor: "#a5e670",
      color: "#000",
      "& td": {
        borderTop: "1px solid #1f2c3c",
        borderBottom: "1px solid #1f2c3c",
      },
    },
    "&.round-type-row": {
      backgroundColor: "#e3f8cf",
      color: "#000",
    },
    "&.deal-row": {
      backgroundColor: "#FFF",
      color: "#000",
      lineHeight: "12.8px",
    },
    "&.spacer-row": {
      height: "5px",
      fontSize: 0,
      lineHeight: 0,
      backgroundColor: "white",
    },
    "&.spacer-row-bottom": {
      height: "30px",
      fontSize: 0,
      lineHeight: 0,
      backgroundColor: "transparent",
    },
  },
  "& tbody": {
    borderBottom: "solid 1px lightgrey",
  },
  "& caption": {
    paddingTop: "0.15rem",
    captionSide: "bottom",
    textAlign: "right",
    fontSize: "90%",
    fontWeight: 300,
    fontStyle: "italic",
  },
});

export const ValuationSourceIndicator = ({ portfolio }) => {
  if (
    !portfolio ||
    portfolio.valuation_source === "LAST_VALUATION" ||
    portfolio.valuation_source === "NOT_NEEDED"
  ) {
    return null;
  }

  const color =
    {
      last_valuation: "green",
      old_valuation: "orange",
      cost: "orange",
      mixed: "red",
    }[portfolio.valuation_source?.toLowerCase() || ""] || "grey";

  const preTitle = `The source of the valuation is ${portfolio.valuation_source?.toLowerCase()}`;
  const title =
    preTitle +
    " " +
    ({
      last_valuation:
        "It means that the valuation presented here is an old one. Maybe you forgot to create a new valuation note. Or maybe the valuation note you created is not at the right date.",
      old_valuation:
        "It means that the valuation presented here is an old one. Maybe you forgot to create a new valuation note. Or maybe the valuation note you created is not at the right date.",
      cost: "The valuation presented is the cost of the line. Maybe you forgot to create a new valuation note.",
      mixed:
        "The valuation presented here is mix of different kinds of valuation (cost of the asset if no valuation is found, old valuation of the asset is no new one can be found, ...). Maybe you forgot to create a new valuation note. Or maybe the valuation note you created is not at the right date.",
    }[portfolio.valuation_source?.toLowerCase()] || "");

  return (
    <Tooltip title={title || ""}>
      <span
        title={portfolio.valuation_source_str}
        style={{
          marginLeft: "2px",
          marginRight: "-2px",
          verticalAlign: "middle",
          marginTop: "-4px",
          marginBottom: "-4px",
        }}
      >
        <CircleIcon sx={{ fontSize: "0.5rem", color: color }} />
      </span>
    </Tooltip>
  );
};

const SummaryPartialLine = ({ portfolio, previous_portfolio }) => {
  const { formatNumber, formatPercentage, formatMultiple, formatDate } =
    useFormatters();

  return (
    <>
      <td className="">{formatNumber(portfolio.invested_amount_in_euro)}</td>
      <td className="">{formatNumber(portfolio.cost_sold_in_euro)}</td>
      <td className="">
        {formatNumber(portfolio.capital_gain_dividend_and_interest_in_euro)}
      </td>
      <td className="">
        {formatNumber(portfolio.capital_gain_equity_in_euro)}
      </td>
      <td className="">{formatNumber(portfolio.realized_cash_in_euro)}</td>
      <td className="">
        {formatNumber(previous_portfolio?.total_value_in_euro)}
      </td>
      <td className="">
        {formatMultiple(previous_portfolio?.valuation_multiple)}
        <ValuationSourceIndicator portfolio={previous_portfolio} />
      </td>
      <td className="">{formatNumber(portfolio.remaining_invested_cost)}</td>
      <td className="">{formatNumber(portfolio.interest_in_euro)}</td>
      <td className="">{formatNumber(portfolio.capital_gain_in_euro)}</td>
      <td className="">{formatNumber(portfolio.value_in_euro)}</td>
      <td className="">{formatNumber(portfolio.total_value_in_euro)}</td>
      <td className="">{formatPercentage(portfolio.valuation_irr)}</td>
      <td className="">
        {formatMultiple(portfolio.valuation_multiple)}
        <ValuationSourceIndicator portfolio={portfolio} />
      </td>
    </>
  );
};

const SummaryTable = forwardRef(({ vizInfo }, ref) => {
  const { t, i18n } = useTranslation("translation");
  const { formatNumber, formatPercentage, formatMultiple, formatDate } =
    useFormatters();

  if (!vizInfo) {
    return <div>No data</div>;
  }

  return (
    <>
      <StyledTable ref={ref}>
        <colgroup>
          <col className="col-first" />
          <col className="col-date" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-thin" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-thin" />
          <col className="col-thin" />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={7} className="hide-span">
              &nbsp;
            </th>
            <th colSpan={2} className="title visible-span">
              {formatDate(vizInfo.previous_valuation_period?.date)}
            </th>
            <th colSpan={7} className="title visible-span">
              {formatDate(vizInfo.valuation_period.date)}
            </th>
          </tr>
          <tr>
            <th className="title text-start">{t("Deal")}</th>
            <th className="title">{t("Date of initial investment")}</th>
            <th className="title" title="invested_amount_in_euro">
              {t("Cost of investment")}
            </th>
            <th className="title" title="cost_sold_in_euro">
              {t("Acquisition sold cost")}
            </th>
            <th
              className="title"
              title="capital_gain_dividend_and_interest_in_euro"
            >
              {t("Dividends & interests")}
            </th>
            <th className="title" title="capital_gain_equity_in_euro">
              {t("Capital gains and losses")}
            </th>
            <th className="title" title="realized_cash_in_euro">
              {t("Total net proceeds")}
            </th>
            <th
              className="title"
              title="previous_portfolio.total_value_in_euro"
            >
              {t("FMV + proceeds")}
            </th>
            <th className="title" title="previous_portfolio.valuation_multiple">
              {t("Gross multiple")}
            </th>
            <th className="title" title="remaining_invested_cost">
              {t("Remaining acquisition cost")}
            </th>
            <th className="title" title="interest_in_euro">
              {t("Accrued interest")}
            </th>
            <th className="title" title="capital_gain_in_euro">
              {t("Capital gains and losses")}
            </th>
            <th className="title" title="value_in_euro">
              {t("Fair market value")}
            </th>
            <th className="title" title="total_value_in_euro">
              {t("FMV + proceeds")}
            </th>
            <th className="title" title="valuation_irr">
              {t("Gross IRR")}
            </th>
            <th className="title" title="valuation_multiple">
              {t("Gross multiple")}
            </th>
          </tr>
          <tr className="spacer-row">
            <td>&nbsp;</td>
          </tr>
        </thead>
        <tbody>
          {vizInfo.tree.by_fund.map((fundNode, fundIndex) => (
            <Fragment key={fundIndex}>
              {fundNode.by_deal_status.map((statusNode, statusIndex) => (
                <Fragment key={statusIndex}>
                  {fundNode.fund.reporting_split_by_deal_type
                    ? statusNode.by_round_type.map(
                        (roundTypeNode, roundTypeIndex) => (
                          <Fragment key={roundTypeIndex}>
                            {roundTypeNode.by_deal.map((dealNode, index) => (
                              <tr className="deal-row" key={index}>
                                <td className="text-start">
                                  {dealNode.portfolio.overriden_deal_name}
                                </td>
                                <td className="">
                                  {formatDate(
                                    dealNode.portfolio.initial_investment_date,
                                  )}
                                </td>
                                <SummaryPartialLine
                                  portfolio={dealNode.portfolio}
                                  previous_portfolio={
                                    dealNode.previous_portfolio
                                  }
                                />
                              </tr>
                            ))}
                            <tr className="round-type-row">
                              <td className="text-start" colSpan={2}>
                                {t("TotalTransactionsRoundType", {
                                  roundType: roundTypeNode.round_type.label,
                                })}
                              </td>
                              <SummaryPartialLine
                                portfolio={roundTypeNode.portfolio}
                                previous_portfolio={
                                  roundTypeNode.previous_portfolio
                                }
                              />
                            </tr>
                          </Fragment>
                        ),
                      )
                    : statusNode.by_deal.map((dealNode, index) => (
                        <tr className="deal-row" key={index}>
                          <td className="text-start">
                            {dealNode.portfolio.overriden_deal_name}
                          </td>
                          <td className="">
                            {formatDate(
                              dealNode.portfolio.initial_investment_date,
                            )}
                          </td>
                          <SummaryPartialLine
                            portfolio={dealNode.portfolio}
                            previous_portfolio={dealNode.previous_portfolio}
                          />
                        </tr>
                      ))}
                  <tr className="spacer-row">
                    <td>&nbsp;</td>
                  </tr>
                  <tr className="deal-status-row">
                    <td className="text-start" colSpan={2}>
                      {t("TotalCompaniesStatus", {
                        status: statusNode.deal_status.label,
                      })}
                    </td>
                    <SummaryPartialLine
                      portfolio={statusNode.portfolio}
                      previous_portfolio={statusNode.previous_portfolio}
                    />
                  </tr>
                  <tr className="spacer-row">
                    <td>&nbsp;</td>
                  </tr>
                </Fragment>
              ))}
              <tr className="fund-row">
                <td className="text-start" colSpan={1}>
                  {fundNode.fund.name}
                </td>
                <td className="col-value">
                  {fundNode.portfolio.initial_investment_date
                    ? dayjs(fundNode.portfolio.initial_investment_date).format(
                        "YYYY-MM-DD",
                      )
                    : ""}
                </td>
                <SummaryPartialLine
                  portfolio={fundNode.portfolio}
                  previous_portfolio={fundNode.previous_portfolio}
                />
              </tr>
            </Fragment>
          ))}
          <tr className="spacer-row-bottom">
            <td>&nbsp;</td>
          </tr>
        </tbody>
      </StyledTable>
    </>
  );
});

export default SummaryTable;
